"use client";

import * as TogglePrimitive from "@radix-ui/react-toggle";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/web/lib/styling";

const toggleVariants = cva(
  "group/toggle cursor-pointer inline-flex items-center justify-center gap-1 rounded-lg text-sm font-medium whitespace-nowrap transition-all outline-none hover:bg-muted hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent hover:bg-muted",
      },
      size: {
        default:
          "h-8 min-w-8 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        sm: "h-7 min-w-7 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 min-w-9 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
      },
      color: {
        default:
          "aria-pressed:bg-withus-dark-blue aria-pressed:text-withus-white data-[state=on]:bg-withus-dark-blue data-[state=on]:text-withus-white data-[state=on]:hover:bg-withus-dark-blue/90",
        red: "aria-pressed:bg-withus-red aria-pressed:text-withus-white data-[state=on]:bg-withus-red data-[state=on]:text-withus-white data-[state=on]:hover:bg-withus-red/90",
        yellow:
          "aria-pressed:bg-withus-yellow aria-pressed:text-withus-blue data-[state=on]:bg-withus-yellow data-[state=on]:text-withus-blue data-[state=on]:hover:bg-withus-yellow/90",
        blue: "aria-pressed:bg-withus-blue aria-pressed:text-withus-white data-[state=on]:bg-withus-blue data-[state=on]:text-withus-white data-[state=on]:hover:bg-withus-blue/90",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      color: "default",
    },
  },
);

function Toggle({
  className,
  variant = "default",
  size = "default",
  color,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> & VariantProps<typeof toggleVariants>) {
  const resolvedColor = color ?? "default";

  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      data-color={resolvedColor !== "default" ? resolvedColor : undefined}
      className={cn(toggleVariants({ variant, size, color: resolvedColor, className }))}
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
